/* eslint-disable no-shadow */
import { Box, Button, DropdownMenu, Loader, Text, toast } from '@acast/decibel';
import {
  EllipsisHRegularGlyph,
  ShareSquareRegularGlyph,
  UnlinkLightGlyph,
} from '@acast/decibel-glyphs';
import * as Sentry from '@sentry/browser';
import { getEpisodeStatus, hasImage, unpublishEpisode } from 'ducks/episodes';
import { fetchShow, selectShow } from 'ducks/shows';
import moment from 'moment';
import T from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { getImage } from 'services/image-api';
import EmbedCodeGenerator from 'shared/components/embed-code-generator';
import Modal from 'shared/components/modal';
import ShareDialog from 'shared/components/share-episode-dialog';
import ListItem from './list-item';

const DEFAULT_THUMBNAIL = '/assets/images/default-show.png';

function unpublishEpisodeWithConfirmation(episodeData, unpublishEpisode) {
  Modal.present({
    title: 'Are you sure?',
    dialog: 'Are you sure you want to unpublish this episode?',
    actions: [
      { name: 'Cancel', value: false, variant: 'tertiary' },
      { name: 'Unpublish', value: true, variant: 'primary', intent: 'danger' },
    ],
  }).then(isConfirmed => {
    if (isConfirmed) {
      unpublishEpisode(episodeData).catch(error => {
        toast.danger(`Error fetching episode stats: ${error.message}`, { closable: true });
        Sentry.captureException(error);
      });
    }
  });
}

const EmbedCodeModalSection = ({ show, episode }) =>
  show && (
    <>
      <Text variant="h4" mt={8}>
        Embed player
      </Text>
      <EmbedCodeGenerator showSlug={show.permalinks[0]} episodeSlug={episode.permalinks[0]} />
    </>
  );

class EpisodesListItem extends Component {
  state = { isLoading: true };

  componentDidMount() {
    this.props
      .fetchShow(this.props.episode.channel)
      .then(() => this.setState({ isLoading: false }))
      .catch(() => this.setState({ isLoading: false }));
  }

  showShareModal = () => {
    const props = { show: this.props.show, episode: this.props.episode };
    Modal.present({
      variant: 'large',
      dialog: (
        <>
          <ShareDialog {...props} />
          <EmbedCodeModalSection {...props} />
        </>
      ),
      actions: Modal.Actions.DONE,
    });
  };

  renderContextMenu = () => {
    const { episode, unpublishEpisode } = this.props;

    if (!episode.published) {
      return null;
    }

    return (
      <Box
        justifyContent="flex-end"
        onClick={event => {
          // Prevent row click
          event.stopPropagation();
        }}
      >
        <DropdownMenu
          placement="bottom-end"
          title="More"
          toggle={
            <Button
              glyph={EllipsisHRegularGlyph}
              variant="tertiary"
              size="small"
              title="Options"
              aria-label={`Options for ${episode.name}`}
            />
          }
        >
          <DropdownMenu.Menu>
            <DropdownMenu.Item glyph={ShareSquareRegularGlyph} onClick={this.showShareModal}>
              Share
            </DropdownMenu.Item>
            <DropdownMenu.Item
              glyph={UnlinkLightGlyph}
              onClick={() => unpublishEpisodeWithConfirmation(episode, unpublishEpisode)}
            >
              Unpublish
            </DropdownMenu.Item>
          </DropdownMenu.Menu>
        </DropdownMenu>
      </Box>
    );
  };

  render() {
    const { episode, onClick } = this.props;
    const inProgress = false; // TODO: isEpisodeBeingPublished(episode.id) from resources/episode looks through state.operations for the episode id
    const status = getEpisodeStatus(episode);
    const displayDate = episode.published ? episode.publishDate : episode.created;

    const thumbnail = (
      <img
        src={hasImage(episode) ? getImage(episode.image, { w: 32 }) : DEFAULT_THUMBNAIL}
        alt=""
      />
    );

    return (
      <ListItem
        thumbnail={thumbnail}
        name={episode.name}
        created={moment(displayDate).format('MMM D, YYYY HH:mm')}
        status={inProgress ? <Loader width={8} /> : status}
        action={this.renderContextMenu()}
        onClick={() => onClick(episode.id)}
      />
    );
  }
}

EpisodesListItem.propTypes = {
  show: T.object,
  episode: T.object.isRequired,
  onClick: T.func.isRequired,
  fetchShow: T.func.isRequired,
  unpublishEpisode: T.func.isRequired,
};

export default connect(
  (state, { episode }) => ({
    show: selectShow(episode.channel)(state),
  }),
  { fetchShow, unpublishEpisode }
)(EpisodesListItem);
